import _, { debounce } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../framework/Button";
import GlobalContext from "../../../framework/GlobalContext";
import Shortcut from "../../../framework/Shortcut";
import Tab from "../../../framework/tab/Tab";
import { fetchBranches } from "../../../services/branch-service";
import {
  ADD_BANK_GENERAL_TAB,
  ADD_BANK_MISCELLANEOUS_TAB,
} from "../../../framework/tab/tabUtilsConstants";
import { displayToast } from "../../../framework/toast/toastUtils";
import { saveBank } from "../../../services/bank-services";
import {
  checkErrorStateExists,
  focusInputField,
  removeErrorState,
} from "../../../utils/utils";
import { createFormState } from "./addBankUtils";
import classes from "./index.module.css";

const AddBank = ({
  onSave = () => {},
  newProduct = null,
  onEscape = () => {},
  editBankId,
}) => {
  const dispatch = useDispatch();
  const { setShowAddProductAutocomplete, setCurrentComponent } =
    useContext(GlobalContext);

  const [focusTab, setFocusTab] = useState(0);
  const [branches, setBranches] = useState([]);

  const addBankButtonRef = useRef();

  const { formState, errorState } = useSelector(
    (state) => state.commonReducer.common.addBank || {}
  );

  const updateErrorStates = useCallback(
    (errorState) => {
      dispatch({
        type: "set_add_bank_error_state",
        payload: errorState,
      });
    },
    [dispatch]
  );

  useEffect(() => {
    fetchBranches(`deleted=false`).then((r) => {
      const opts = r.result.rows.map((x) => {
        return {
          ...x,
          id: x.code,
          label: x.code,
        };
      });
      const sortedOpts = opts?.sort((x, y) => (x.region > y.region ? 1 : -1));
      setBranches(sortedOpts);
    });
  }, []);

  const validateForm = useCallback(
    (fieldName, data) => {
      const _formState = { ...data };
      let updatedErrorState = errorState;

      if (!fieldName || fieldName === "add_bank_name") {
        if (!_formState?.bankName?.trim()) {
          if (!checkErrorStateExists("add_bank_name", errorState)) {
            updatedErrorState.push({ add_bank_name: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_bank_name");
          displayToast({
            type: "error",
            title: "Please enter a bank name",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_bank_name", errorState)) {
            updatedErrorState = removeErrorState(
              "add_bank_name",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }

      if (!fieldName || fieldName === "add_bank_account_number") {
        if (_formState?.accountNo && _formState?.accountNo.trim() === "") {
          if (!checkErrorStateExists("add_bank_account_number", errorState)) {
            updatedErrorState.push({ add_bank_account_number: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_bank_account_number");
          displayToast({
            type: "error",
            title: "Please enter a bank account number",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_bank_account_number", errorState)) {
            updatedErrorState = removeErrorState(
              "add_bank_account_number",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }

      if (!fieldName || fieldName === "add_bank_ifsc_code") {
        if (_formState?.ifsc && _formState?.ifsc.trim() === "") {
          if (!checkErrorStateExists("add_bank_ifsc_code", errorState)) {
            updatedErrorState.push({ add_bank_ifsc_code: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_bank_ifsc_code");
          displayToast({
            type: "error",
            title: "Please enter a valid IFSC code",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_bank_ifsc_code", errorState)) {
            updatedErrorState = removeErrorState(
              "add_bank_ifsc_code",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }

      if (!fieldName || fieldName === "Permanentgstin") {
        const gstin = _formState?.gstin?.trim();
        const gstinRegex = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/;

        if (gstin && !gstinRegex.test(gstin)) {
          displayToast({
            type: "error",
            title: "Please enter a valid permanent GSTIN.",
          });
          if (!checkErrorStateExists("add_bank_permanent_gstin", errorState)) {
            updatedErrorState.push({ add_bank_permanent_gstin: true });
          }
          if (!fieldName) {
            focusInputField(updatedErrorState);
          }
          return false;
        }
        if (checkErrorStateExists("add_bank_permanent_gstin", errorState)) {
          updatedErrorState = removeErrorState(
            "add_bank_permanent_gstin",
            updatedErrorState,
            errorState
          );
        }
      }
      if (!fieldName || fieldName === "provisionalGstin") {
        const gstin = _formState?.provisionalGstin?.trim();
        const gstinRegex = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/;

        if (gstin && !gstinRegex.test(gstin)) {
          displayToast({
            type: "error",
            title: "Please enter a valid provisional GSTIN.",
          });
          if (
            !checkErrorStateExists("add_bank_provisional_gstin", errorState)
          ) {
            updatedErrorState.push({ add_bank_provisional_gstin: true });
          }
          if (!fieldName) {
            focusInputField(updatedErrorState);
          }
          return false;
        }
        if (checkErrorStateExists("add_bank_provisional_gstin", errorState)) {
          updatedErrorState = removeErrorState(
            "add_bank_provisional_gstin",
            updatedErrorState,
            errorState
          );
        }
      }
      if (!fieldName || fieldName === "email") {
        const regex =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (_formState?.bankEmail && !regex.test(_formState?.bankEmail)) {
          displayToast({
            type: "error",
            title: "Please enter a valid email.",
          });
          if (!checkErrorStateExists("add_bank_email", errorState)) {
            updatedErrorState.push({ add_bank_email: true });
          }
          if (!fieldName) {
            focusInputField(updatedErrorState);
          }
          return false;
        }
        if (checkErrorStateExists("add_bank_email", errorState)) {
          updatedErrorState = removeErrorState(
            "add_bank_email",
            updatedErrorState,
            errorState
          );
        }
      }
      if (!fieldName || fieldName === "add_bank_branch") {
        if (!_formState.branchCode || _formState.branchCode.length === 0) {
          if (!checkErrorStateExists("add_bank_branch", updatedErrorState)) {
            updatedErrorState.push({ add_bank_branch: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_bank_branch");
          displayToast({
            type: "error",
            title: "Please select at least one Branch.",
          });
          return false;
        } else {
          if (checkErrorStateExists("add_bank_branch", updatedErrorState)) {
            updatedErrorState = removeErrorState(
              "add_bank_branch",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }
      if (!fieldName || fieldName === "pincode") {
        if (_formState?.pincode && _formState.pincode.toString().length !== 6) {
          displayToast({
            type: "error",
            title: "Pincode must be at least 6 digits long.",
          });
          if (!checkErrorStateExists("add_bank_pin_code", errorState)) {
            updatedErrorState.push({ add_bank_pin_code: true });
          }
          updateErrorStates(updatedErrorState);
          focusInputField("add_bank_pin_code");
          return false;
        } else {
          if (checkErrorStateExists("add_bank_pin_code", errorState)) {
            updatedErrorState = removeErrorState(
              "add_bank_pin_code",
              updatedErrorState,
              errorState
            );
            updateErrorStates(updatedErrorState);
          }
        }
      }

      // if (!fieldName || fieldName === 'add_bank_gst_type') {
      //   if (!_formState.gstinType || !_formState.gstinType.label) {
      //     if (!checkErrorStateExists('add_bank_gst_type', updatedErrorState)) {
      //       updatedErrorState.push({ 'add_bank_gst_type': true });
      //     }
      //     updateErrorStates(updatedErrorState);
      //     focusInputField('add_bank_gst_type');
      //     displayToast({
      //       type: 'error',
      //       title: 'Please select a valid GST Type.',
      //     });
      //     return false;
      //   } else {
      //     if (checkErrorStateExists('add_bank_gst_type', updatedErrorState)) {
      //       updatedErrorState = removeErrorState('add_bank_gst_type', updatedErrorState, errorState);
      //       updateErrorStates(updatedErrorState);
      //     }
      //   }
      // }
      return true;
    },
    [errorState, updateErrorStates]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceValidateForm = useCallback(_.debounce(validateForm, 300), []);

  const saveFormHandler = useCallback(async () => {
    if (validateForm(null, formState)) {
      // Create localFormState with fields that exist in createFormState
      const localFormState = createFormState({
        bankId: formState?.bankId,
        bankName: formState?.bankName,
        shortName: formState?.shortName,
        bankCode: formState?.bankCode,
        accountNo: formState?.accountNo,
        micrCode: formState?.micrCode,
        ifsc: formState?.ifsc,
        discription: formState?.discription,
        note: formState?.note,
        gstin: formState?.gstin,
        provisionalGstin: formState?.provisionalGstin,
        bankAddress: formState?.bankAddress,
        locality: formState?.locality,
        pincode: formState?.pincode,
        bankEmail: formState?.bankEmail,
        city: formState?.city,
        area: formState?.area,
        region: formState?.region,
        country: formState?.country,
        zone: formState?.zone,
        branchCode: formState?.branchCode,
        gstinType: formState?.gstinType,
      });
      await saveBank(localFormState)
        .then((response) => {
          if (response.status !== "success") {
            displayToast({
              type: "error",
              title: response,
            });
            return;
          }
          displayToast({
            type: "success",
            title: "Bank added successfully",
          });
          onSave(response.result);
          setShowAddProductAutocomplete(false);
        })
        .catch(() => {});
    }
  }, [validateForm, formState, onSave, setShowAddProductAutocomplete]);

  const debounceAndSave = debounce(saveFormHandler, 300);

  const resetForm = useCallback(() => {
    dispatch({
      type: "reset_add_bank_form_state",
    });
    updateErrorStates([]);
  }, [dispatch, updateErrorStates]);

  const escapeHandler = useCallback(() => {
    onEscape();
    resetForm();
  }, [onEscape, resetForm]);

  const keyupListener = useCallback(
    (e) => {
      const key = e.key?.toLowerCase();
      const altKey = e.altKey;
      const ctrlKey = e.ctrlKey;
      if (altKey) {
        if ([1, 2].includes(+key)) {
          setFocusTab(+key - 1);
          return;
        }
      }
      if (key === "escape") {
        escapeHandler();
        return;
      }
      if (ctrlKey && key === "s") {
        addBankButtonRef.current.click();
        return;
      }
    },
    [escapeHandler]
  );

  const removeErrorStateOnChange = useCallback(
    (id) => {
      let updatedErrorState = errorState;
      if (checkErrorStateExists(id, errorState)) {
        updatedErrorState = removeErrorState(id, updatedErrorState, errorState);
        updateErrorStates(updatedErrorState);
      }
    },
    [updateErrorStates, errorState]
  );

  const tabs = useMemo(() => {
    return [
      {
        type: ADD_BANK_GENERAL_TAB,
        label: "General",
        showCloseIcon: false,
        props: {
          editBankId,
          removeErrorStateOnChange,
          resetForm,
          branches,
          validateForm: debounceValidateForm,
        },
        endAvatar: <Shortcut keys={["Alt", "1"]} />,
      },
      {
        type: ADD_BANK_MISCELLANEOUS_TAB,
        label: "Miscellaneous",
        showCloseIcon: false,
        props: {
          editBankId,
          removeErrorStateOnChange,
          branches,
          validateForm: debounceValidateForm,
          resetForm,
        },
        endAvatar: <Shortcut keys={["Alt", "2"]} />,
      },
    ];
  }, [
    branches,
    debounceValidateForm,
    editBankId,
    removeErrorStateOnChange,
    resetForm,
  ]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  useEffect(() => {
    if (keyupListener) {
      document.addEventListener("keyup", keyupListener, false);
      return () => {
        document.removeEventListener("keyup", keyupListener, false);
      };
    }
  }, [keyupListener]);

  useEffect(() => {
    return () => {
      debounceValidateForm.cancel();
      resetForm();
    };
  }, [resetForm, debounceValidateForm]);

  return (
    <div className={classes["add-bank-container"]}>
      <div className={classes["add-bank-header"]}>
        <div className={classes["add-bank-header-title"]}>
          {`${formState?.bankName ? formState?.bankName : ""}`}
        </div>

        <div className={classes["add-bank-header-shortcuts"]}>
          <Shortcut keys={["Esc"]} label="Cancel" />
        </div>
      </div>
      <div className={classes["add-bank-tabs"]}>
        <Tab
          tabs={tabs}
          focusTabIndex={focusTab}
          showCloseIcon={false}
          tabClicked={(index) => {
            setFocusTab(index);
          }}
        />
      </div>
      <div className={classes["add-bank-button-container"]}>
        <Button
          label={"Save Bank"}
          color="violet"
          ref={addBankButtonRef}
          endIcon={<Shortcut keys={["Ctrl", "S"]} />}
          onFocus={() => setCurrentComponent("add_bank_save_button")}
          onClick={debounceAndSave}
        />
      </div>
    </div>
  );
};

export default AddBank;
